import { useCallback, useMemo } from "react";
import { Alert, Box, CircularProgress, Stack, useTheme } from "@mui/material";
import { FoodsCheckbox } from "@foodpilot/foods";
import { MRT_RowData } from "material-react-table";
import { useTranslation } from "react-i18next";

import { CustomMRTFilter as IngredientReferenceMRTFilter } from "../mrt.types";
import { SearchBar } from "src/components/SearchBar/SearchBar";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useGetAllIngredientReferencesUnified } from "src/api/client-api/ingredientReferenceUnified/useGetAllIngredientReferencesUnified";
import { useGetSomeIngredientReferencesUnified } from "src/api/client-api/ingredientReferenceUnified/useGetSomeIngredientReferencesUnified";
import { IngredientReferenceUnified } from "src/api/client-api/ingredientReferenceUnified/types";

const getCompositeId = (item: IngredientReferenceUnified) => item.compositeId;
const getReferenceName = (item: IngredientReferenceUnified) => item.name;
const MAX_FILTER_WIDTH_RATIO = 1.5;

type IngredientReferenceFilterProps<T extends MRT_RowData> = {
    mrtProps: IngredientReferenceMRTFilter<T>;
};

export const IngredientReferenceFilter = <T extends MRT_RowData>(props: IngredientReferenceFilterProps<T>) => {
    const { mrtProps } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    const rawFilterValue = mrtProps.column.getFilterValue();
    const currentFilters = useMemo<string[]>(() => {
        if (Array.isArray(rawFilterValue)) return rawFilterValue.map(String);
        if (typeof rawFilterValue === "string" && rawFilterValue !== "") return [rawFilterValue];
        return [];
    }, [rawFilterValue]);

    const searchApi = useDelayedSearch();

    const {
        ingredientReferences: searchResults = [],
        isLoading: isSearchLoading,
        isError: isSearchError,
        isDegraded,
    } = useGetAllIngredientReferencesUnified({
        q: searchApi.delayedSearch,
        itemsPerPage: 50,
        // Without this the sure-api catalog floods the dropdown with values that
        // always yield zero matches against the user's ingredients.
        usedByIngredients: true,
    });

    // Resolve names of items already selected so they keep showing even when filtered out
    // by the current search term.
    const {
        ingredientReferences: selectedItemsResolved = [],
        isLoading: isSelectedLoading,
        isError: isSelectedError,
    } = useGetSomeIngredientReferencesUnified(currentFilters.length > 0 ? currentFilters : undefined);

    const options = useMemo<IngredientReferenceUnified[]>(() => {
        const byId = new Map<string, IngredientReferenceUnified>();
        for (const item of selectedItemsResolved) {
            byId.set(item.compositeId, item);
        }
        for (const item of searchResults) {
            if (!byId.has(item.compositeId)) {
                byId.set(item.compositeId, item);
            }
        }
        return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [searchResults, selectedItemsResolved]);

    const selectedItems = useMemo(() => {
        const filterSet = new Set(currentFilters);
        return options.filter((item) => filterSet.has(item.compositeId));
    }, [options, currentFilters]);

    const handleChange = useCallback(
        (newSelected: IngredientReferenceUnified[]) => {
            if (newSelected.length === 0) {
                mrtProps.column.setFilterValue(undefined);
                return;
            }
            mrtProps.column.setFilterValue(newSelected.map((item) => item.compositeId));
        },
        [mrtProps.column],
    );

    const isLoading = isSearchLoading || isSelectedLoading;
    const hasError = isSearchError || isSelectedError;
    const filterMaxWidth = mrtProps.column.getSize() * MAX_FILTER_WIDTH_RATIO;

    return (
        <Stack gap={theme.spacing(2)} maxWidth={`${filterMaxWidth}px`}>
            <Stack>
                <SearchBar
                    search={searchApi.search}
                    setSearch={searchApi.setSearch}
                    placeholder={t("ingredientReferences.search", "Search references...")}
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- filter popover opens on user action
                    autoFocus
                />
            </Stack>
            <HorizontalBar />
            <Stack
                paddingLeft={theme.spacing(1)}
                paddingRight={theme.spacing(1)}
                paddingY={theme.spacing(0.5)}
                maxHeight="300px"
                overflow="auto"
                sx={{
                    boxSizing: "border-box",
                    scrollbarGutter: "stable",
                    "& .MuiFormControlLabel-root": {
                        alignItems: "flex-start",
                        marginRight: 0,
                    },
                    "& .MuiFormControlLabel-label": {
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                    },
                }}
            >
                {hasError ?
                    <Alert severity="error">{t("ingredientReferences.loadError", "Failed to load references.")}</Alert>
                :   <>
                        {isDegraded ?
                            <Alert severity="warning" sx={{ marginBottom: theme.spacing(1) }}>
                                {t(
                                    "ingredientReferences.degraded",
                                    "Results may be incomplete. Refine your search to see all references.",
                                )}
                            </Alert>
                        :   null}
                        {isLoading && options.length === 0 ?
                            <Stack alignItems="center" padding={theme.spacing(2)}>
                                <CircularProgress size="20px" />
                            </Stack>
                        :   <FoodsCheckbox
                                values={options}
                                selectedItems={selectedItems}
                                getId={getCompositeId}
                                getName={getReferenceName}
                                onChange={handleChange}
                            />
                        }
                    </>
                }
            </Stack>
        </Stack>
    );
};

const HorizontalBar = () => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                width: "70%",
                alignSelf: "center",
                borderBottom: `1px solid ${theme.custom.grey[500]}`,
            }}
        />
    );
};
